import { useDataStore } from '@/pinia'
import { GetLanguage } from '@/utils-ts'
import { fromUTC, toUTC } from '@/utils-ts/DateUtils'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/id'
import 'dayjs/locale/pl'
import 'dayjs/locale/pt'
import 'dayjs/locale/ru'
import 'dayjs/locale/th'
import 'dayjs/locale/tr'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)

export const JsonToFormData = (json: any, expandArray: boolean | null = true) => {
  const formData = new FormData()

  const processKey = (key: string, value: any) => {
    if (Array.isArray(value) && expandArray) {
      value.forEach((item: any, index: number) => {
        if (typeof item === 'object' && item !== null) {
          for (const subKey in item) {
            formData.append(`${key}[${index}].${subKey}`, item[subKey])
          }
        } else {
          formData.append(`${key}[${index}]`, item)
        }
      })
    } else if (value !== null && value !== undefined) {
      formData.append(key, value)
    }
  }

  for (const key in json) {
    processKey(key, json[key])
  }

  return formData
}
export const JsonToFormDataSimple = (json: any, expandArray: boolean | null = true) => {
  const formData = new FormData()
  for (const key in json) {
    if (Array.isArray(json[key]) && expandArray) {
      json[key].forEach((item: any) => {
        formData.append(`${key}`, item)
      })
    } else if (json[key] !== null && json[key] !== undefined) {
      formData.append(key, json[key])
    }
  }
  return formData
}

/**
 * UTC tarihi aktif dile göre yerelleştirip formatlar. Ekrana gösterim için kullan.
 * @param format dayjs formatı ('L', 'D MMM', 'HH:mm' vb.)
 * @param fromNow "3 saat önce" / "3 sa" tarzı göreli çıktı
 * @param short göreli çıktıda eki ("önce"/"ago") atla
 */
export const DateTimeParser = (
  date: string,
  format: string = 'L',
  fromNow: boolean = false,
  short: boolean = false,
) => {
  if (!date) return null

  const locale = GetLanguage().code
  const parsedDate = dayjs.utc(date).local().locale(locale)
  if (fromNow) {
    return parsedDate.fromNow(short)
  }
  return parsedDate.format(format)
}

/** Bir haftalık aralığı yerelleştirilmiş okunur metne çevirir ("5-11 Haz"). */
export const FormatWeekRange = (dateStr: string) => {
  const start = dayjs.utc(dateStr).local()
  const end = dayjs.utc(dateStr).local().add(6, 'day')

  if (start.month() === end.month()) {
    return `${start.format('D')}-${DateTimeParser(end.toISOString(), 'D MMM')}`
  } else {
    return `${DateTimeParser(start.toISOString(), 'D MMM')}-${DateTimeParser(end.toISOString(), 'D MMM')}`
  }
}

/**
 * @deprecated Backend UTC string → Local Date için `fromUTC` kullan.
 * Geriye dönük uyumluluk için korunuyor; artık `fromUTC`'ye delege eder.
 */
export const LocalTimeConverter = (date: string): Date => {
  if (!date) return new Date()
  return fromUTC(date)
}

/**
 * @deprecated Giden tarihler için `toUTC` kullan.
 * Geriye dönük uyumluluk için korunuyor; her zaman UTC ISO string ('Z') döner,
 * asla offset'li (+03:00) string üretmez.
 */
export const DateTimeOffset = (date: string | Date, _isWithTimezone: boolean = false): string => {
  if (!date) return ''
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return ''
  return toUTC(parsed)
}

export const DateConverter = (date: Date, plusDay: number, setEndDate: boolean = false) => {
  if (!date) return ''
  const converted = new Date(date)
  converted.setDate(converted.getDate() + plusDay)
  if (setEndDate) {
    // Yerel gün sonu → UTC'ye çevrilir
    converted.setHours(23, 59, 59, 0)
  }

  return toUTC(converted)
}

export const MoneyFormat = (money: number) => {
  return Intl.NumberFormat('de-DE').format(money)
}

/** Kullanıcı metnini v-html ile basmadan önce HTML karakterlerini kaçırır. */
export const EscapeHtml = (text: string): string => {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Metin içindeki basit biçimlendirme işaretlerini HTML etiketlerine çevirir.
 * `*kalın*` → <b>, `-italik-` → <i>, `_altı çizgili_` → <u>
 * İşaretin kelime içinde kalan hâlleri (tarih-aralığı, snake_case vb.) dönüştürülmez.
 */
export const TextFormatParser = (text: string): string => {
  if (!text) return text

  const patterns: [RegExp, string][] = [
    [/(?<![\wğüşöçıİĞÜŞÖÇ*])\*(?!\s)([^*\n]*[^\s*])\*(?![\wğüşöçıİĞÜŞÖÇ*])/g, 'b'],
    [/(?<![\wğüşöçıİĞÜŞÖÇ-])-(?!\s)([^-\n]*[^\s-])-(?![\wğüşöçıİĞÜŞÖÇ-])/g, 'i'],
    [/(?<![\wğüşöçıİĞÜŞÖÇ_])_(?!\s)([^_\n]*[^\s_])_(?![\wğüşöçıİĞÜŞÖÇ_])/g, 'u'],
  ]

  return patterns.reduce(
    (acc, [regex, tag]) =>
      acc.replace(regex, (_match, content: string) => `<${tag}>${content}</${tag}>`),
    text,
  )
}

export const PostParser = (post: any): string | null => {
  if (!post) return null

  const employeeRegex = /@{([\wğüşöçİĞÜŞÖÇ-]+)}/g
  post.postText = post.postText.replace(employeeRegex, (match: string, employeeId: string) => {
    const employee = post.employees.find((x: any) => x.id === employeeId)
    return employee ? `@${employee.profileName}` : match
  })

  const valueRegex = /#{([\wğüşöçİĞÜŞÖÇ-]+)}/g
  let replacedPostText = post.postText.replace(valueRegex, (match: string, valueId: string) => {
    const value = post.companyValues.find((x: any) => x.id == valueId)
    return value ? `#${value.name}` : match
  })

  const pointRegex = /\+{([\wğüşöçİĞÜŞÖÇ-]+)}/g
  replacedPostText = replacedPostText.replace(pointRegex, (_match: string, point: string) => point)

  const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)

  const blacklist = post.blacklistKeyword?.map((x: string) => escapeRegex(x.toLowerCase())) || []

  if (blacklist.length > 0) {
    const regex = new RegExp(String.raw`\b(${blacklist.join('|')})\b`, 'gi')

    replacedPostText = replacedPostText.replace(
      regex,
      (match: string) => `<span style="background:yellow">${match}</span>`,
    )
  }

  return TextFormatParser(replacedPostText)
}

export const CommentParser = (c: any) => {
  let comment = c.comment
  const list = new Set(c.blacklistKeyword.map((x: string) => x.toLowerCase()))

  if (!comment) {
    return null
  }
  comment.split(' ').forEach((word: string) => {
    if (list.has(word.toLowerCase())) {
      comment = comment.replace(word, `<span style="background:yellow">${word}</span>`)
    }
  })
  return TextFormatParser(comment)
}

export const Integer = (event: KeyboardEvent) => {
  const invalidChars = ['-', '+', 'e', '.', ',']

  if (invalidChars.includes(event.key)) {
    event.preventDefault()
  }
}

export const HexToRGB = (hex: string) => {
  if (!hex) return null
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, function (_m, r, g, b) {
    return r + r + g + g + b + b
  })

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: Number.parseInt(result[1] ?? '0', 16),
        g: Number.parseInt(result[2] ?? '0', 16),
        b: Number.parseInt(result[3] ?? '0', 16),
      }
    : null
}

export const ImageToBlob = (url: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
        return response.blob()
      })
      .then(blob => {
        resolve(blob)
      })
      .catch(error => {
        reject(error)
      })
  })
}

export const GenerateRandomColor = () => {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

export const PermissionController = async (key: string): Promise<boolean> => {
  if (!key) return false
  const list = await useDataStore().GetFlattenList()
  return list.includes(key)
}
